import { Icon, Icons } from "@sonic/CustomComponents/headless/elements/icon";
import { BaseProps } from "@sonic/CustomComponents/headless/models/BaseProps";
import { breakpointsForMediaQuery } from "@sonic/CustomComponents/headless/utils/Breakpoints";
import { colors } from "@sonic/CustomComponents/headless/utils/Colors";
import React from "react";
import styled, { css, FlattenSimpleInterpolation } from "styled-components";

interface Props extends BaseProps {
    title: string;
    active?: boolean;
    disabled?: boolean;
    children?: JSX.Element | JSX.Element[];
    showTitle?: boolean;
    icon?: {
        iconName?: Icons;
        iconColor?: string;
        iconWidth?: number;
        iconHeight?: number;
    };
    url?: string;
    type?: ButtonTypes | LinkTypes;
    size?: "medium" | "large";
    buttonType?: "reset" | "button" | "submit";
    classNames?: string;
    onClick?: (event: React.SyntheticEvent<Element, Event>) => void;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    onBlur?: (event: React.SyntheticEvent<Element, Event>) => void;
    customStyles?: FlattenSimpleInterpolation;
    textAlignment?: "left" | "center" | "right";
}

type ButtonTypes =
    | "primary"
    | "primaryRound"
    | "text"
    | "navigationCard"
    | "secondaryPNB"
    | "secondaryPWB"
    | "quarternaryMPQ"
    | "primaryRoundButton"
    | "secondaryRoundButton";
type LinkTypes = "submenu";

export const buttonStyles = {
    primary: css`
        font-family: "Pancetta W05 Bold", sans-serif;
        padding: 0;
        display: flex;

        &:hover {
            color: #e30613;

            svg {
                color: #e30613;
            }
        }
    `,
    submenu: css`
        line-height: normal;
        color: #000;
        padding: 14px;
        border-bottom: 1px solid #ededed;
        font-family: "Pancetta W05 Bold", sans-serif;

        @media ${breakpointsForMediaQuery.md} {
            border-bottom: none;
            max-width: 350px;
        }

        &:hover {
            color: #000;
            background-color: #ededed;
        }
    `,
    text: css`
        font-family: "Pancetta W05 Regular", sans-serif;
        text-align: left;
    `,
    primaryRound: css`
        color: #706f6f;
        border-radius: 50%;
        width: 44px;
        height: 44px;
        background-color: #fff;
        border: 1px solid #bdbcbc;
        align-items: center;
        justify-content: center;

        &:hover {
            background-color: #ededed;

            svg {
                fill: #ededed;
            }
        }

        &:disabled {
            border-color: #ededed;
            color: #ededed;

            svg {
                fill: #ededed;
            }
        }
    `,
    primaryRoundButton: css`
        color: #fff;
        border-radius: 20px;
        width: 100%;
        height: 40px;
        background-color: #e30613;
        align-items: center;
        justify-content: center;
        font-family: "Pancetta W05 Bold Italic", sans-serif;
        font-size: 16px;
        gap: 1px;
        text-transform: uppercase;
        padding: 15px 20px 13px 24px;

        @media ${breakpointsForMediaQuery.md} {
            font-size: 14px;
            width: auto;
        }

        &:hover,
        &:active,
        &:focus {
            background-color: #af1616;
        }

        &:disabled {
            border-color: #ededed;
            color: #ededed;
            opacity: 0.2;
        }
    `,
    secondaryRoundButton: css`
        color: #000;
        border-radius: 20px;
        border: 1px solid #bdbcbc;
        width: 100%;
        height: 40px;
        align-items: center;
        justify-content: center;
        font-family: "Pancetta W05 Bold Italic", sans-serif;
        gap: 1px;
        font-size: 16px;
        text-transform: uppercase;
        padding: 14px 22px;

        @media ${breakpointsForMediaQuery.md} {
            font-size: 14px;
            display: inline-flex;
            width: auto;
        }

        &:hover {
            background-color: #ededed;

            svg {
                fill: #af1616;
            }
        }

        &:disabled {
            border-color: #ededed;
            color: #ededed;
            opacity: 0.2;

            svg {
                fill: #ededed;
            }
        }
    `,
    navigationCard: css`
        display: flex;
        align-items: flex-start;
    `,
    secondaryPNB: css`
        text-transform: uppercase;
        font-size: 16px;
        display: flex;
        align-items: center;
        gap: 3px;
        color: #000;
        font-family: "Pancetta W05 Bold Italic", sans-serif;
        background-color: #fff;
        padding: 15px 20px 13px 24px;
        border-radius: 22px;
        width: 100%;

        @media ${breakpointsForMediaQuery.md} {
            width: max-content;
        }

        &:hover {
            background-color: #ededed;
        }
    `,
    secondaryPWB: css`
        text-transform: uppercase;
        font-size: 16px;
        display: flex;
        align-items: center;
        gap: 3px;
        color: #000;
        font-family: "Pancetta W05 Bold Italic", sans-serif;
        background-color: #fff;
        border: solid 1.5px #bdbcbc;
        padding: 15px 20px 13px 24px;
        border-radius: 22px;
        width: 100%;

        &:hover {
            background-color: #ededed;
        }
    `,
    quarternaryMPQ: css`
        font-family: "Pancetta W05 Bold Italic", sans-serif;
        color: #e30613;
        font-size: 14px;
        text-transform: uppercase;
    `,
};

const Wrapper = styled.button<{
    styles?: FlattenSimpleInterpolation;
    customStyles?: FlattenSimpleInterpolation;
    active?: boolean;
    textAlignment?: string;
    size: string;
}>`
    appearance: none;
    text-decoration: none;
    border: none;
    background-color: transparent;

    font-size: 18px;
    padding: 0;
    display: flex;
    gap: 4px;
    align-items: center;
    color: #000;
    cursor: pointer;
    line-height: 16px;

    svg {
        transition: transform 0.5s ease;
    }

    ${({ active }) =>
        active &&
        `
            color: #e30613;

            :not(.login) svg {
                transform: rotate(180deg);
            }
        `}

    ${({ textAlignment }) =>
        textAlignment &&
        `
            text-align: ${textAlignment};
            justify-content: ${textAlignment};
    `}

    ${({ styles }) =>
        `
        ${styles}
    `}

    ${({ customStyles }) =>
        `
        ${customStyles}
    `}

    ${({ size }) =>
        size === "medium" &&
        `
            font-size: 14px;
            line-height: 14px;
            padding: 14px 22px 12px;
    `}

    &:disabled {
        pointer-events: none;
        opacity: 0.2;
    }

    &.search-active {
        svg {
            color: #e30613;
        }
    }
`;
const Link = styled.a<{
    styles?: FlattenSimpleInterpolation;
    customStyles?: FlattenSimpleInterpolation;
    textAlignment?: Props["textAlignment"];
    size: Props["size"];
}>`
    color: #000;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    line-height: 16px;

    &:hover {
        color: #e30613;
    }

    &:visited {
        color: #000;
    }

    ${({ textAlignment }) =>
        textAlignment &&
        `
            text-align: ${textAlignment};
            justify-content: ${textAlignment};
        `}

    ${({ styles }) =>
        `
        ${styles}
    `}

    ${({ customStyles }) =>
        `
        ${customStyles}
    `}

    ${({ size }) =>
        size === "medium" &&
        `
        font-size: 14px;
        line-height: 14px;
        padding: 14px 22px 12px;
    `}
`;

export const Button = ({
    active,
    classNames,
    children,
    disabled,
    icon,
    url,
    title,
    showTitle = true,
    type,
    customStyles,
    onClick,
    onKeyDown,
    onBlur,
    textAlignment,
    size = "large",
    buttonType = "button",
    testSelector,
}: Props) => {
    const styles = buttonStyles[type ?? "primary"];

    if (url) {
        return (
            <Link
                styles={styles}
                customStyles={customStyles}
                className={classNames}
                textAlignment={textAlignment}
                size={size}
                onClick={onClick}
                href={url}
                title={title}
            >
                {showTitle && title}
                {icon?.iconName && (
                    <Icon color={icon.iconColor} width={icon.iconWidth} height={icon.iconHeight} icon={icon.iconName} />
                )}
                {children}
            </Link>
        );
    }
    return (
        <Wrapper
            data-test-selector={testSelector}
            className={`${classNames ?? ""}`}
            onClick={onClick}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            aria-label={title}
            active={active}
            textAlignment={textAlignment}
            size={size}
            styles={styles}
            customStyles={customStyles}
            type={buttonType}
            disabled={disabled}
        >
            {showTitle && title}
            {icon?.iconName && (
                <Icon color={icon.iconColor} width={icon.iconWidth} height={icon.iconHeight} icon={icon.iconName} />
            )}
            {children}
        </Wrapper>
    );
};
